import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const EmailVerificationPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error, expired
  const [message, setMessage] = useState(''); // message brut renvoyé par le backend (affiché tel quel)
  const [invalidLink, setInvalidLink] = useState(false); // cas « pas de token » (affichage uniquement)

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setInvalidLink(true);
      return;
    }

    axios.post(`${API}/api/auth/verify-email`, { token })
      .then(res => {
        setStatus('success');
        setMessage(res.data.message || '');
      })
      .catch(err => {
        const detail = err.response?.data?.detail || '';
        if (detail.includes('expire')) {
          setStatus('expired');
        } else {
          setStatus('error');
        }
        setMessage(detail);
      });
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4" data-testid="email-verification-page">
      <div className="grid-pattern"></div>
      <div className="glass p-8 w-full max-w-md fade-in text-center">
        {status === 'loading' && (
          <div data-testid="verify-loading">
            <Loader2 className="w-16 h-16 text-cyan-400 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">{t('emailVerif.loadingTitle')}</h2>
            <p className="text-gray-400 text-sm">{t('emailVerif.loadingSub')}</p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="verify-success">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">{t('emailVerif.successTitle')}</h2>
            <p className="text-gray-400 text-sm mb-6">{message || t('emailVerif.successDefault')}</p>
            <Link
              to="/login/client"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white btn-cyan"
              data-testid="verify-go-login"
            >
              {t('emailVerif.login')}
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div data-testid="verify-error">
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">{t('emailVerif.errorTitle')}</h2>
            <p className="text-gray-400 text-sm mb-6">{invalidLink ? t('emailVerif.invalidLink') : (message || t('emailVerif.errorDefault'))}</p>
            <Link
              to="/login/client"
              className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
              data-testid="verify-back-login"
            >
              <ArrowLeft className="w-4 h-4" /> {t('emailVerif.backLogin')}
            </Link>
          </div>
        )}

        {status === 'expired' && (
          <div data-testid="verify-expired">
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-10 h-10 text-amber-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">{t('emailVerif.expiredTitle')}</h2>
            <p className="text-gray-400 text-sm mb-6">{message || t('emailVerif.expiredDefault')}</p>
            <Link
              to="/login/client"
              className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
              data-testid="verify-expired-login"
            >
              <ArrowLeft className="w-4 h-4" /> {t('emailVerif.backLoginResend')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationPage;
